import React, { useState } from 'react';
import axios from 'axios';
import { useInfiniteQuery } from 'react-query';
import HBox from '@/components/layout/HBox';
import VBox from '@/components/layout/VBox';
import Botao from '@/components/Botao';
import DataTable, { DataTableHeader } from '@/components/layout/DataTable';
import { mdiAccountMultiplePlusOutline, mdiPlusNetworkOutline, mdiCancel, mdiCheckCircle, mdiChevronDown, mdiChevronUp, mdiFilter, mdiMonitor, mdiRadar, mdiShield, mdiShieldPlusOutline, mdiWeb, mdiWifi } from '@mdi/js';
import type { AccessReportRowDTO, AccessReportFilterDTO, AccessAction, ReportType } from '@webip/shared/src/dto/access_report';
import { useAppliance } from '@/lib/useAppliance';
import Icon from '@mdi/react';
import LayoutUsuario from '@/components/navegacao/LayoutUsuario';
import PageHeader from '@/components/layout/PageHeader';
import Badge from '@/components/layout/Badge';
import CampoData from '@/components/Formulario/CampoData';
import CampoTexto from '@/components/Formulario/CampoTexto';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import CampoSeletorLivre from '@/components/Formulario/CampoSeletorLivre';
import CampoCheckbox from '@/components/Formulario/CampoCheckbox';
import Grid, { GridItem } from '@/components/layout/Grid';
import { useAuth } from '@/lib/useAuth';
import { DispositivoDaLinhaModal, GrupoDaLinhaModal, RegraDaLinhaModal } from './AcoesAcessoModal';
import { linhaAceitaRegra } from './acoes';

type ResponseAccessLogs = {
    ok: boolean;
    items: AccessReportRowDTO[];
    total: number;
    totalIsLowerBound?: boolean;
    offset: number;
    appliedFilters: AccessReportFilterDTO;
    generatedAt: string;
    error?: string;
};

const ACTION_ICON: Record<AccessAction, string> = {
    permitido: mdiCheckCircle,
    bloqueado: mdiCancel,
};

const ACTION_COLOR: Record<AccessAction, string> = {
    permitido: 'green',
    bloqueado: 'red',
};

const TYPE_LABEL: Record<ReportType, string> = {
    fwip: 'Firewall',
    hotspot: 'Hotspot',
    proxy: 'Proxy',
    scan: 'Scan de portas', // desativado — só pra cobrir o tipo, não é listado
    dispositivo: 'Dispositivo',
};

const TYPE_COLOR: Record<ReportType, string> = {
    fwip: '#3b82f6',      // azul
    hotspot: '#f59e0b',   // laranja
    proxy: '#6b7280',     // cinza
    scan: '#dc2626',      // vermelho
    dispositivo: '#0d9488', // teal
};

const ICON_VARIANT: Record<ReportType, string> = {
    fwip: mdiShield,
    hotspot: mdiWifi,
    proxy: mdiWeb,
    scan: mdiRadar,
    dispositivo: mdiMonitor,
};

const formatBytes = (n: number): string => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

interface FilterState {
    startDate?: string;
    endDate?: string;
    originIp?: string;
    originDomain?: string;
    destinationIp?: string;
    destinationDomain?: string;
    port?: string;
    protocol?: string;
    type?: ReportType | '';
    action?: AccessAction | '';
    // undefined quando desmarcado, pra não contar como filtro ativo
    originWithoutDevice?: true;
    defaultAction?: true;
}

export function AccessReportsPage() {
    const PAGE_SIZE = 50;
    const { selectedAppliance } = useAppliance();
    const [draftFilters, setDraftFilters] = useState<FilterState>({});
    const [appliedFilters, setAppliedFilters] = useState<FilterState>({});
    const [filtersOpen, setFiltersOpen] = useState(false);
    // Ações por linha (criar regra, incluir em grupo): só ADMIN escreve em
    // política e grupo — as rotas exigem o mesmo.
    const { user } = useAuth();
    const podeAgir = user?.papel === 'ADMIN';
    const [linhaRegra, setLinhaRegra] = useState<AccessReportRowDTO | null>(null);
    const [linhaGrupo, setLinhaGrupo] = useState<AccessReportRowDTO | null>(null);
    const [linhaDispositivo, setLinhaDispositivo] = useState<AccessReportRowDTO | null>(null);

    const activeFilterCount = Object.values(appliedFilters).filter(v => v !== undefined && v !== '').length;

    const queryAccessLogs = useInfiniteQuery({
        queryKey: ['access_logs', selectedAppliance?.id, appliedFilters],
        queryFn: async ({ pageParam = 0 }) => {
            const filters: AccessReportFilterDTO = {
                startDate: appliedFilters.startDate,
                endDate: appliedFilters.endDate,
                originIp: appliedFilters.originIp,
                originDomain: appliedFilters.originDomain,
                destinationIp: appliedFilters.destinationIp,
                destinationDomain: appliedFilters.destinationDomain,
                port: appliedFilters.port ? Number(appliedFilters.port) : undefined,
                protocol: appliedFilters.protocol || undefined,
                type: appliedFilters.type || undefined,
                action: appliedFilters.action || undefined,
                originWithoutDevice: appliedFilters.originWithoutDevice,
                defaultAction: appliedFilters.defaultAction,
            };

            const params = new URLSearchParams({
                applianceId: String(selectedAppliance!.id),
                limit: String(PAGE_SIZE),
                offset: String(pageParam),
                filters: JSON.stringify(filters),
            });

            return await axios.get<ResponseAccessLogs>(
                `/api/report/access-logs?${params}`,
                { withCredentials: true }
            );
        },
        getNextPageParam: (lastPage) => {
            const current = lastPage.data.offset;
            const total = lastPage.data.total;
            // Total com teto: além dele, continua paginando até vir página curta.
            if (lastPage.data.totalIsLowerBound) {
                return lastPage.data.items.length === PAGE_SIZE ? current + PAGE_SIZE : undefined;
            }
            return current + PAGE_SIZE < total ? current + PAGE_SIZE : undefined;
        },
        enabled: !!selectedAppliance?.id,
    });

    const allLogs = queryAccessLogs.data?.pages?.flatMap(p => p.data.items ?? []) ?? [];
    const total = queryAccessLogs.data?.pages?.[0]?.data?.total ?? 0;
    const totalIsLowerBound = queryAccessLogs.data?.pages?.[0]?.data?.totalIsLowerBound ?? false;

    const handleApplyFilters = () => {
        setAppliedFilters(draftFilters);
        setFiltersOpen(false);
    };

    const handleResetFilters = () => {
        setDraftFilters({});
        setAppliedFilters({});
    };

    const handleLoadMore = () => {
        if (queryAccessLogs.hasNextPage && !queryAccessLogs.isFetchingNextPage) {
            queryAccessLogs.fetchNextPage();
        }
    };

    const headers: DataTableHeader[] = [
        {
            label: 'Data/Hora',
            width: '100px',
            render: (row: AccessReportRowDTO) => {
                const d = new Date(row.timestamp);
                return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR');
            },
        },
        {
            label: 'Status',
            width: '40px',
            alignCenter: true,
            render: (row: AccessReportRowDTO) => (
                <div>
                    <Icon path={ICON_VARIANT[row.type]} size={.75} title={TYPE_LABEL[row.type]} color={TYPE_COLOR[row.type]} />
                    <Icon path={ACTION_ICON[row.action]} size={.75} title={row.action} color={ACTION_COLOR[row.action]} />
                </div>
            ),
        },
        {
            label: 'Origem',
            width: '100px',
            // `user` nas linhas de firewall é o usuário de hotspot logado no IP na
            // hora do evento (resolvido no backend) — o IP fica no tooltip.
            render: (row: AccessReportRowDTO) => <div title={row.originIp}>{row.user || row.lanDeviceName || row.originDomain || row.originIp || '—'}</div>,
        },
        {
            label: 'Destino',
            width: '220px',
            render: (row: AccessReportRowDTO) => {
                // Hotspot sem destino = o login em si; com destino é navegação
                // do usuário (fluxo IPFIX atribuído à sessão) e cai no formato
                // comum abaixo.
                if (row.type === 'hotspot' && !row.destinationIp && !row.destinationDomain) {
                    return <div title={row.device ? `MAC ${row.device}` : undefined}>
                        <span style={{ opacity: .7 }}>login em </span>{row.hotspotName || 'hotspot'}
                    </div>;
                }
                const fonte = row.destinationDomain ? row.destinationDomainSource : undefined;
                const titulo = [
                    fonte === 'sni' ? 'Nome pedido pelo cliente (SNI do TLS)' : fonte === 'ptr' ? 'DNS reverso do IP (em CDN/nuvem, o nome do provedor)' : null,
                    row.destinationDomain && row.destinationIp ? `IP ${row.destinationIp}` : null,
                    row.bytesTransferred ? `${formatBytes(row.bytesTransferred)} transferidos` : null,
                ].filter(Boolean).join(' · ');
                return <div title={titulo || undefined}>
                    {fonte && <span style={{ fontSize: '.7em', opacity: .6, marginRight: 4, textTransform: 'uppercase' }}>{fonte}</span>}
                    <span>{row.destinationDomain || row.destinationIp || '—'}</span>
                    <span>{row.port ? `:${row.port}` : ''}</span>
                    <span>{row.protocol ? ` /${row.protocol}` : ''}</span>
                </div>;
            },
        },
        {
            label: 'Política',
            width: '100px',
            render: (row: AccessReportRowDTO) => row.policyName || '—',
        },
        {
            label: 'ASN',
            width: '130px',
            render: (row: AccessReportRowDTO) => {
                if (!row.asnName && !row.country) return '—';
                return (
                    <span title={row.asnName}>
                        {row.country ? `[${row.country}] ` : ''}{row.asnName || '—'}
                    </span>
                );
            },
        },
        {
            label: 'Qtd',
            width: '50px',
            render: (row: AccessReportRowDTO) => row.hits || '—',
        },
        ...(podeAgir ? [{
            label: '',
            width: '56px',
            render: (row: AccessReportRowDTO) => {
                const origemLan = !!row.originIp && row.type !== 'hotspot';
                return (
                    <HBox gap={2}>
                        {linhaAceitaRegra(row) && row.originIp && (
                            <Botao size="sm" variant="outline-secondary" icon={mdiShieldPlusOutline} iconProps={{ size: 0.6 }}
                                title="Criar regra para este destino" onClick={() => setLinhaRegra(row)} />
                        )}
                        {origemLan && row.lanDeviceId && (
                            <Botao size="sm" variant="outline-secondary" icon={mdiAccountMultiplePlusOutline} iconProps={{ size: 0.6 }}
                                title="Incluir dispositivo em grupo" onClick={() => setLinhaGrupo(row)} />
                        )}
                        {/* `direction` só é real em scan (firewall vem sempre "inbound");
                            scan de entrada tem origem externa, não vira dispositivo. */}
                        {origemLan && !row.lanDeviceId && row.applianceId && !(row.type === 'scan' && row.direction === 'inbound') && (
                            <Botao size="sm" variant="outline-secondary" icon={mdiPlusNetworkOutline} iconProps={{ size: 0.6 }}
                                title="Cadastrar como dispositivo" onClick={() => setLinhaDispositivo(row)} />
                        )}
                    </HBox>
                );
            },
        } satisfies DataTableHeader] : []),
    ];

    return (
        <LayoutUsuario>
            <VBox gap="md" className="p-lg" style={{ height: '100%', minHeight: 0 }}>
                <PageHeader
                    title={`Relatórios de Acesso${selectedAppliance ? ` — ${selectedAppliance.nome}` : ''}`}
                    description="Visualize e filtre os eventos de acesso registrados pelo appliance."
                />

                <VBox style={{ flex: 1, minHeight: 0 }}>
                    {/* Barra de resultados + botão de filtros */}
                    <HBox style={{ alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ fontSize: '0.875rem', fontWeight: 600, opacity: 0.8 }}>
                            {total}{totalIsLowerBound ? '+' : ''} resultado{total !== 1 ? 's' : ''}
                            {activeFilterCount > 0 && (
                                <span style={{
                                    marginLeft: '8px',
                                    fontSize: '0.75rem',
                                    background: 'var(--color-primary, #3b82f6)',
                                    color: '#fff',
                                    borderRadius: '10px',
                                    padding: '1px 7px',
                                    fontWeight: 700,
                                }}>
                                    {activeFilterCount} filtro{activeFilterCount !== 1 ? 's' : ''}
                                </span>
                            )}
                        </span>
                        <Botao
                            variant={filtersOpen ? 'outline-primary' : activeFilterCount > 0 ? 'outline-primary' : 'outline-secondary'}
                            size="sm"
                            icon={mdiFilter}
                            iconProps={{ size: 0.7 }}
                            onClick={() => setFiltersOpen(v => !v)}
                        >
                            Filtros
                            <Icon path={filtersOpen ? mdiChevronUp : mdiChevronDown} size={0.6} style={{ marginLeft: '4px' }} />
                        </Botao>
                    </HBox>

                    {/* Painel de filtros colapsável */}
                    {filtersOpen && (
                        <VBox style={{
                            background: 'var(--color-surface, rgba(255,255,255,0.04))',
                            border: '1px solid var(--color-border, rgba(255,255,255,0.1))',
                            borderRadius: '8px',
                            padding: '16px',
                            marginBottom: '12px',
                        }}>
                            <Grid style={{ marginBottom: '12px' }}>
                                <GridItem p={6} md={3} lg={2}>
                                    <CampoData
                                        label="Data Início"
                                        type="datetime-local"
                                        value={draftFilters.startDate || ''}
                                        onChange={(v) => setDraftFilters({ ...draftFilters, startDate: v })}
                                    />
                                </GridItem>

                                <GridItem p={6} md={3} lg={2}>
                                    <CampoData
                                        label="Data Fim"
                                        type="datetime-local"
                                        value={draftFilters.endDate || ''}
                                        onChange={(v) => setDraftFilters({ ...draftFilters, endDate: v })}
                                    />
                                </GridItem>

                                <GridItem p={6} md={3} lg={2}>
                                    <CampoTexto
                                        label="IP Origem"
                                        placeholder="192.168.1.10"
                                        value={draftFilters.originIp || ''}
                                        onChange={(v) => setDraftFilters({ ...draftFilters, originIp: v })}
                                    />
                                </GridItem>

                                <GridItem p={6} md={3} lg={2}>
                                    <CampoTexto
                                        label="IP Destino"
                                        placeholder="8.8.8.8"
                                        value={draftFilters.destinationIp || ''}
                                        onChange={(v) => setDraftFilters({ ...draftFilters, destinationIp: v })}
                                    />
                                </GridItem>

                                <GridItem p={6} md={3} lg={2}>
                                    <CampoTexto
                                        label="Porta"
                                        type="number"
                                        placeholder="80"
                                        value={draftFilters.port || ''}
                                        onChange={(v) => setDraftFilters({ ...draftFilters, port: v })}
                                    />
                                </GridItem>

                                <GridItem p={6} md={3} lg={2}>
                                    <CampoSeletorLivre
                                        label="Protocolo"
                                        placeholder="tcp, udp…"
                                        value={draftFilters.protocol || null}
                                        onChange={(v) => setDraftFilters({ ...draftFilters, protocol: v || '' })}
                                        options={[
                                            { id: 'tcp', label: 'TCP' },
                                            { id: 'udp', label: 'UDP' },
                                            { id: 'icmp', label: 'ICMP' },
                                        ]}
                                        nullable
                                    />
                                </GridItem>

                                <GridItem p={6} md={3} lg={2}>
                                    <CampoSeletor
                                        label="Tipo"
                                        value={draftFilters.type || null}
                                        onChange={(v) => setDraftFilters({ ...draftFilters, type: (v as ReportType) || '' })}
                                        options={[
                                            { id: 'fwip', label: 'Firewall' },
                                            { id: 'hotspot', label: 'Hotspot' },
                                            { id: 'proxy', label: 'Proxy' },
                                            { id: 'dispositivo', label: 'Dispositivo' },
                                        ]}
                                        nullable
                                        noSearch
                                        placeholder="Todos"
                                    />
                                </GridItem>

                                <GridItem p={6} md={3} lg={2}>
                                    <CampoSeletor
                                        label="Ação"
                                        value={draftFilters.action || null}
                                        onChange={(v) => setDraftFilters({ ...draftFilters, action: (v as AccessAction) || '' })}
                                        options={[
                                            { id: 'permitido', label: 'Permitido' },
                                            { id: 'bloqueado', label: 'Bloqueado' },
                                        ]}
                                        nullable
                                        noSearch
                                        placeholder="Todos"
                                    />
                                </GridItem>

                                <GridItem p={6} md={3} lg={2}>
                                    <CampoTexto
                                        label="Domínio Origem"
                                        placeholder="example.com"
                                        value={draftFilters.originDomain || ''}
                                        onChange={(v) => setDraftFilters({ ...draftFilters, originDomain: v })}
                                    />
                                </GridItem>

                                <GridItem p={6} md={3} lg={2}>
                                    <CampoTexto
                                        label="Domínio Destino"
                                        placeholder="api.google.com"
                                        value={draftFilters.destinationDomain || ''}
                                        onChange={(v) => setDraftFilters({ ...draftFilters, destinationDomain: v })}
                                    />
                                </GridItem>

                                <GridItem p={12} md={6} lg={4}>
                                    <CampoCheckbox
                                        text="Só IPs sem dispositivo cadastrado"
                                        value={!!draftFilters.originWithoutDevice}
                                        onChange={(v) => setDraftFilters({ ...draftFilters, originWithoutDevice: v || undefined })}
                                    />
                                    <CampoCheckbox
                                        text="Só ação padrão (sem política)"
                                        hint="Eventos de firewall de origens que nenhuma política cobre."
                                        value={!!draftFilters.defaultAction}
                                        onChange={(v) => setDraftFilters({ ...draftFilters, defaultAction: v || undefined })}
                                    />
                                </GridItem>
                            </Grid>

                            <HBox gap="sm">
                                <Botao
                                    onClick={handleApplyFilters}
                                    icon={mdiFilter}
                                    disabled={queryAccessLogs.isFetching}
                                >
                                    Aplicar
                                </Botao>
                                <Botao
                                    onClick={handleResetFilters}
                                    variant="secondary"
                                    disabled={queryAccessLogs.isFetching}
                                >
                                    Limpar
                                </Botao>
                            </HBox>
                        </VBox>
                    )}

                    {queryAccessLogs.isError && (
                        <Badge variant="danger">
                            {(queryAccessLogs.error as any)?.response?.data?.error
                                ?? 'Erro ao carregar relatório. Tente novamente.'}
                        </Badge>
                    )}

                    <DataTable
                        rowSize='sm'
                        cabecalhos={headers}
                        linhas={allLogs}
                        hasMoreData={!!queryAccessLogs.hasNextPage}
                        onLoadMore={handleLoadMore}
                        isLoading={queryAccessLogs.isFetchingNextPage}
                    />

                    <RegraDaLinhaModal
                        row={linhaRegra}
                        https={selectedAppliance?.agent_type !== 'opnsense'}
                        onClose={() => setLinhaRegra(null)}
                    />
                    <GrupoDaLinhaModal row={linhaGrupo} onClose={() => setLinhaGrupo(null)} />
                    <DispositivoDaLinhaModal row={linhaDispositivo} onClose={() => setLinhaDispositivo(null)} />
                </VBox>
            </VBox>
        </LayoutUsuario>
    );
}

export default AccessReportsPage;
